import { _ } from "../../polyglot/polyglot.js";
import {
	angleDownSolidIcon,
	angleUpSolidIcon,
	editRegularIcon,
} from "../../elements/icons/index.js";
import { formatFullWithNoDecimals } from "../../helpers/currency-formatting-util.js";
import { stacker } from "../../elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import WaitingButtonWidget from "../WaitingButtonWidget/WaitingButtonWidget.js";

/**
 * Widget to show an existing manual journal entry.
 *
 * It renders the entry's name as well as its transactions.
 * It also renders actions to interact with the entry (edit/delete).
 *
 * @param {ManualJournalEntryRuleModel} manualJournalEntryRuleModel -
 * @param {Renderable} [legend] - Extra information about the rule. It is
 *   rendered next to the value
 * @param {string} [currentAccountNumber] - Account number of the row used to
 *   render this widget
 * @param {function} [onAccountSelectionRequest] - Callback invoked
 *   with an account number when a cell selection is requested
 * @param {function} [onEdit] - Callback invoked when "Edit" is clicked
 * @param {function} [onDelete] - Callback invoked when "Delete" is clicked
 * @param {boolean} [shouldShowEditActions] - Signals whether editing actions are needed or not
 */
export default class ManualJournalEntryViewer extends Widget {
	constructor({
		manualJournalEntryRuleModel,
		legend,
		currentAccountNumber,
		onAccountSelectionRequest = () => {},
		onEdit = () => {},
		onDelete = () => {},
		shouldShowEditActions,
	}) {
		super(...arguments);

		this._manualJournalEntryRuleModel = manualJournalEntryRuleModel;
		this._legend = legend;
		this._onEdit = onEdit;
		this._onDelete = onDelete;
		this._currentAccountNumber = currentAccountNumber;
		this._onAccountSelectionRequest = onAccountSelectionRequest;
		this._shouldShowEditActions = shouldShowEditActions;

		this._expanded = false;
	}

	collapse() {
		this._expanded = false;
		this.update();
	}

	expand() {
		this._expanded = true;
		this.update();
	}

	renderContentOn(html) {
		html.div(
			{ class: "ManualJournalEntryViewer" },
			this._renderHeaderOn.bind(this),
			this._renderBodyOn.bind(this),
		);
	}

	_renderHeaderOn(html) {
		html.div(
			{ class: "viewer-header" },
			html.div(
				{ class: "name", "data-test": "name" },
				this._renderTitleOn.bind(this),
			),
			html.div(
				{ class: "actions" },
				this._renderCollapseButtonOn.bind(this),
				this._renderActionsGroupOn.bind(this),
			),
		);
	}

	_renderTitleOn(html) {
		const numberOfRollingMonths =
			this._manualJournalEntryRuleModel.getNumberOfRollingMonths();
		const name = this._manualJournalEntryRuleModel.getName();

		if (numberOfRollingMonths === 1) {
			html.render(`${name} (${_("rolling one month")})`);
			return;
		}

		if (numberOfRollingMonths === null) {
			html.render(`${name} (${_("rolling infinitely")})`);
			return;
		}

		html.render(
			`${name} (${_("rolling {rollingMonths} months", {
				rollingMonths: numberOfRollingMonths,
			})})`,
		);
	}

	_renderBodyOn(html) {
		if (this._expanded) {
			this._renderExpandedBodyOn(html);
			return;
		}

		this._renderCollapsedBodyOn(html);
	}

	_renderCollapseButtonOn(html) {
		html.div(
			{
				class: "action",
				"data-test": "collapse-action",
				click: () => {
					this._expanded = !this._expanded;
					this.update();
				},
			},
			this._expanded ? angleDownSolidIcon : angleUpSolidIcon,
		);
	}

	_renderActionsGroupOn(html) {
		if (!this._shouldShowEditActions) return;
		const dropdownId = `menu-dropdown-${this._id}`;

		html.div(
			{
				class: "btn-group",
				role: "group",
			},
			html.div(
				{
					class: "btn-group",
					role: "group",
				},
				html.div(
					{
						id: dropdownId,
						class: "actions-drawer dropdown-toggle",
						"data-test": "action-button",
						"data-toggle": "dropdown",
						"aria-haspopup": "true",
					},
					html.i({ class: "fas fa-ellipsis" }),
				),
				html.div(
					{
						class: "actions-menu dropdown-menu",
						"aria-labelledby": dropdownId,
					},
					this._renderActionMenuItemsOn(html),
				),
			),
		);
	}

	_renderCollapsedBodyOn(html) {
		const transaction =
			this._manualJournalEntryRuleModel.getTransactionForAccountNumber(
				this._currentAccountNumber,
			);

		if (!transaction) return;

		const value = transaction.getValue();

		html.div(
			{ class: "collapsed", "data-test": "collapsed" },
			stacker(
				{ spacing: "between" },
				this._valueRenderer(value),
				this._renderLegendOn.bind(this),
			),
		);
	}

	_valueRenderer(value) {
		return value >= 0
			? _("Debit: {value}", {
					value: formatFullWithNoDecimals(value),
				})
			: _("Credit: {value}", {
					value: formatFullWithNoDecimals(-value),
				});
	}

	_renderLegendOn(html) {
		if (!this._legend) return;

		html.div({ "data-test": "legend" }, this._legend);
	}

	_renderExpandedBodyOn(html) {
		html.ul(
			{ class: "transactions" },
			html.li(
				{ class: "transactions-header" },
				html.span(
					html.div({ class: "transaction-label" }, (html) =>
						html.raw("&nbsp;"),
					),
				),
				html.span(_("Debit")),
				html.span(_("Credit")),
			),
			this._manualJournalEntryRuleModel
				.getTransactions()
				.map((transaction) =>
					this._transactionRenderer({ transaction }),
				),
		);
	}

	_transactionRenderer({ transaction }) {
		return (html) => {
			html.li(
				{ class: "transaction" },
				html.div(
					transaction.isForAccountNumber(this._currentAccountNumber)
						? this._currentAccountNumberAccountRenderer({
								transaction,
							})
						: this._linkAccountRenderer({ transaction }),
				),
				html.div(
					{ class: "debit value", "data-test": "value" },
					formatFullWithNoDecimals(transaction.getDebit()) ?? "",
				),
				html.div(
					{ class: "credit value", "data-test": "value" },
					formatFullWithNoDecimals(transaction.getCredit()) ?? "",
				),
			);
		};
	}

	_currentAccountNumberAccountRenderer({ transaction }) {
		return (html) => {
			html.div(
				{ class: "account transaction-label", "data-test": "account" },
				transaction.getAccount().toString(),
			);
		};
	}

	_linkAccountRenderer({ transaction }) {
		return new WaitingButtonWidget({
			label: transaction.getAccount().toString(),
			isAnchor: true,
			cssClass: "account transaction-label",
			dataTestName: "account",
			onClick: () =>
				new Promise((resolve) =>
					setTimeout(() => {
						this._onAccountSelectionRequest({
							accountNumber: transaction.getAccount().getNumber(),
						});

						resolve();
					}, 0),
				),
		});
	}

	_renderActionMenuItemsOn(html) {
		return [
			html.div(
				{
					"data-test": "edit-action",
					class: "action-menu-item dropdown-item icon-and-text",
					click: this._onEdit,
				},
				editRegularIcon,
				_("Edit"),
			),
			html.div(
				{
					"data-test": "delete-action",
					class: "action-menu-item danger dropdown-item icon-and-text",
					click: this._onDelete,
				},
				html.i({ class: "fas fa-xmark" }),
				_("Delete"),
			),
		];
	}
}
